if (!customElements.get('scrolling-promotion')) {
  class ScrollingPromotion extends HTMLElement {
    connectedCallback() {
      if (this.hasInitialized) return;
      this.hasInitialized = true;

      this.scrollMode = this.dataset.scrollMode || 'auto';
      this.promotion = this.querySelector('.promotion');
      if (!this.promotion) return;

      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (this.scrollMode === 'manual') {
        this.initManualSlider();
      } else {
        this.initAutoScroll();
      }
    }

    disconnectedCallback() {
      this.stopAnimation();
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    /* ── Automatic marquee ─────────────────────────────────────────────── */

    initAutoScroll() {
      // Deliberately NOT using the theme's .promotion--animated CSS keyframes.
      // Those are gated behind media queries that never match on touch devices.
      this.style.display = 'block';
      this.style.overflow = 'hidden';
      this.promotion.style.display = 'flex';
      this.promotion.style.flexWrap = 'nowrap';
      this.promotion.style.width = 'max-content';
      this.promotion.style.willChange = 'transform';

      this.originalItems = Array.from(this.promotion.children);
      if (!this.originalItems.length) return;

      this.scrollDirection = this.classList.contains('scrolling-promotion--right') ? 1 : -1;
      this.offset = 0;

      this.fillTrack();
      this.bindAutoScrollEvents();
      this.startAnimation();
    }

    // Duplicate the original items until the strip is at least twice the
    // container width, so the reset point is never visible.
    fillTrack() {
      this.cycleWidth = this.measureCycleWidth();
      if (!this.cycleWidth) return;

      const targetWidth = this.offsetWidth * 2 + this.cycleWidth;
      let currentWidth = this.cycleWidth;
      let guard = 0;

      while (currentWidth < targetWidth && guard < 50) {
        this.originalItems.forEach((item) => {
          this.promotion.appendChild(item.cloneNode(true));
        });
        currentWidth += this.cycleWidth;
        guard += 1;
      }
    }

    measureCycleWidth() {
      let total = 0;
      this.originalItems.forEach((item) => {
        const itemStyle = window.getComputedStyle(item);
        total += item.offsetWidth
          + parseFloat(itemStyle.marginLeft || 0)
          + parseFloat(itemStyle.marginRight || 0);
      });
      const trackStyle = window.getComputedStyle(this.promotion);
      const columnGap = parseFloat(trackStyle.columnGap) || 0;
      return total + columnGap * this.originalItems.length;
    }

    bindAutoScrollEvents() {
      // Pause when scrolled out of view. Starts running regardless, so a
      // misbehaving observer can never leave the marquee stuck.
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.startAnimation();
              } else {
                this.stopAnimation();
              }
            });
          },
          { rootMargin: '200px 0px 200px 0px' }
        );
        this.visibilityObserver.observe(this);
      }

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stopAnimation();
        } else {
          this.startAnimation();
        }
      });

      if (this.hasAttribute('data-pause-on-hover') && window.matchMedia('(hover: hover)').matches) {
        this.addEventListener('mouseenter', () => this.stopAnimation());
        this.addEventListener('mouseleave', () => this.startAnimation());
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          const measured = this.measureCycleWidth();
          if (measured) this.cycleWidth = measured;
        });
        this.resizeObserver.observe(this);
      }

      // Images arriving late change the strip width.
      this.querySelectorAll('img').forEach((image) => {
        if (image.complete) return;
        image.addEventListener('load', () => {
          const measured = this.measureCycleWidth();
          if (measured) this.cycleWidth = measured;
        }, { once: true });
      });
    }

    // Pixels per second, derived from the section's --duration so the
    // existing Speed setting keeps working.
    getPixelsPerSecond() {
      const durationRaw = window.getComputedStyle(this).getPropertyValue('--duration').trim();
      const durationSeconds = parseFloat(durationRaw) || 6;
      return this.cycleWidth / durationSeconds;
    }

    startAnimation() {
      if (this.animationFrame || !this.cycleWidth) return;

      let previousTimestamp = null;

      const step = (timestamp) => {
        if (previousTimestamp === null) previousTimestamp = timestamp;
        const elapsed = (timestamp - previousTimestamp) / 1000;
        previousTimestamp = timestamp;

        this.offset += this.scrollDirection * this.getPixelsPerSecond() * elapsed;

        if (this.offset <= -this.cycleWidth) this.offset += this.cycleWidth;
        if (this.offset >= this.cycleWidth) this.offset -= this.cycleWidth;

        this.promotion.style.transform = 'translate3d(' + this.offset.toFixed(2) + 'px, 0, 0)';
        this.animationFrame = requestAnimationFrame(step);
      };

      this.animationFrame = requestAnimationFrame(step);
    }

    stopAnimation() {
      if (!this.animationFrame) return;
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    /* ── Manual slider (native scroll-snap) ────────────────────────────── */

    initManualSlider() {
      this.slides = Array.from(this.promotion.children);
      if (!this.slides.length) return;

      this.manualItems = parseInt(this.dataset.manualItems, 10) || 4;
      this.paginationEl = this.querySelector('.swiper-pagination');

      this.style.display = 'block';
      this.promotion.style.display = 'flex';
      this.promotion.style.flexWrap = 'nowrap';
      this.promotion.style.overflowX = 'auto';
      this.promotion.style.scrollSnapType = 'x mandatory';
      this.promotion.style.scrollBehavior = 'smooth';
      this.promotion.style.webkitOverflowScrolling = 'touch';
      this.promotion.style.scrollbarWidth = 'none';
      this.promotion.style.msOverflowStyle = 'none';

      if (!document.getElementById('promotion-scrollbar-style')) {
        const styleEl = document.createElement('style');
        styleEl.id = 'promotion-scrollbar-style';
        styleEl.textContent = 'scrolling-promotion .promotion::-webkit-scrollbar{display:none}';
        document.head.appendChild(styleEl);
      }

      this.layoutSlides();
      this.buildPagination();

      this.promotion.addEventListener('scroll', () => {
        if (this.scrollTicking) return;
        this.scrollTicking = true;
        requestAnimationFrame(() => {
          this.updateActiveDot();
          this.scrollTicking = false;
        });
      }, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.layoutSlides();
          this.buildPagination();
        });
        this.resizeObserver.observe(this);
      }

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', (event) => {
          if (event.detail.sectionId !== this.dataset.sectionId) return;
          const slideIndex = this.slides.indexOf(event.target);
          if (slideIndex > -1) this.scrollToSlide(slideIndex);
        });
      }
    }

    getSlidesPerView() {
      const viewportWidth = window.innerWidth;
      if (viewportWidth >= 1280) return this.manualItems;
      if (viewportWidth >= 768) return Math.min(this.manualItems, 3);
      if (viewportWidth >= 640) return 2;
      return 1;
    }

    getGapValue() {
      const isMobile = window.innerWidth < 768;
      const propertyName = isMobile ? '--h-spacing-mobile' : '--h-spacing';
      const rawValue = window.getComputedStyle(this).getPropertyValue(propertyName).trim();
      return parseFloat(rawValue) || 20;
    }

    layoutSlides() {
      const slidesPerView = this.getSlidesPerView();
      const gapValue = this.getGapValue();

      this.promotion.style.gap = gapValue + 'px';

      this.slides.forEach((slide) => {
        slide.style.flex = '0 0 calc((100% - ' + (gapValue * (slidesPerView - 1)) + 'px) / ' + slidesPerView + ')';
        slide.style.scrollSnapAlign = 'start';
        slide.style.minWidth = '0';
      });
    }

    getPageCount() {
      return Math.max(1, this.slides.length - this.getSlidesPerView() + 1);
    }

    buildPagination() {
      if (!this.paginationEl) return;

      const pageCount = this.getPageCount();

      if (pageCount <= 1) {
        this.paginationEl.innerHTML = '';
        this.paginationEl.style.display = 'none';
        return;
      }

      this.paginationEl.style.display = '';

      if (this.paginationEl.children.length === pageCount) {
        this.updateActiveDot();
        return;
      }

      this.paginationEl.innerHTML = '';

      for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'swiper-pagination-bullet';
        dot.setAttribute('aria-label', 'Go to slide ' + (pageIndex + 1));
        dot.dataset.index = pageIndex;
        dot.addEventListener('click', () => this.scrollToSlide(pageIndex));
        this.paginationEl.appendChild(dot);
      }

      this.updateActiveDot();
    }

    scrollToSlide(slideIndex) {
      const targetSlide = this.slides[slideIndex];
      if (!targetSlide) return;
      this.promotion.scrollTo({ left: targetSlide.offsetLeft - this.promotion.offsetLeft, behavior: 'smooth' });
    }

    updateActiveDot() {
      if (!this.paginationEl || !this.paginationEl.children.length) return;

      const slideWidth = this.slides[0].offsetWidth + this.getGapValue();
      const activeIndex = Math.round(this.promotion.scrollLeft / slideWidth);

      Array.from(this.paginationEl.children).forEach((dot, dotIndex) => {
        dot.classList.toggle('swiper-pagination-bullet-active', dotIndex === activeIndex);
      });
    }
  }

  customElements.define('scrolling-promotion', ScrollingPromotion);
}